import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import { I18n } from "i18n-js"
import locales from "../locales.json"
import { subscribe } from "../lib/cable"

Chart.register(...registerables)

const WINDOW_MS = 60 * 60 * 1000
const SCROLL_MS = 250
const SERIES_KEYS = [
  ["team_chat", "messages_per_minute"],
  ["email", "inbound_per_minute"],
  ["email", "outbound_per_minute"],
  ["ai_chat", "user_per_minute"],
  ["ai_chat", "assistant_per_minute"],
  ["ai_chat", "new_conversations_per_minute"],
  ["messenger", "user_per_minute"],
  ["messenger", "bot_per_minute"],
  ["support", "inbound_per_minute"],
  ["support", "reply_per_minute"],
  ["call_center", "concurrent_per_minute"]
]

export default class extends Controller {
  static targets = [
    "status",
    "teamChatChart", "teamChatOnline",
    "emailChart", "emailInbound", "emailOutbound",
    "usersOnline", "usersSessions",
    "aiChart", "aiMessages", "aiCost",
    "messengerChart", "messengerActive", "messengerHuman",
    "supportChart", "supportOpen", "supportUnassigned",
    "queuesChart", "queuesStatus", "queuesList",
    "callCounts", "callChart", "callSessions"
  ]

  connect() {
    this.i18n = new I18n(locales)
    this.i18n.locale = document.querySelector("meta[name='locale']")?.content || "hu"
    this.charts = {}
    this.state = null
    this._onVisibility = () => {
      if (document.visibilityState === "visible") this._onScrollTick()
    }
    document.addEventListener("visibilitychange", this._onVisibility)
    this._subscribe()
    this._startScroll()
  }

  disconnect() {
    this._stopScroll()
    if (this._onVisibility) {
      document.removeEventListener("visibilitychange", this._onVisibility)
      this._onVisibility = null
    }
    this.subscription?.unsubscribe()
    this.subscription = null
    Object.values(this.charts).forEach((c) => c.destroy())
    this.charts = {}
  }

  t(key) {
    return this.i18n.t(`admin.dashboard.${key}`)
  }

  _startScroll() {
    this._stopScroll()
    this.scrollTimer = window.setInterval(() => this._onScrollTick(), SCROLL_MS)
  }

  _stopScroll() {
    if (this.scrollTimer) {
      window.clearInterval(this.scrollTimer)
      this.scrollTimer = null
    }
  }

  _onScrollTick() {
    if (!this.state) return
    if (document.visibilityState === "hidden") return
    const advanced = this._ensureCurrentMinute()
    if (advanced) {
      this._refreshAllCharts()
      return
    }
    this._slideViewport()
  }

  _slideViewport() {
    const now = Date.now()
    const min = now - WINDOW_MS
    Object.values(this.charts).forEach((chart) => {
      if (!chart?.options?.scales?.x) return
      chart.options.scales.x.min = min
      chart.options.scales.x.max = now
      chart.data.datasets.forEach((ds) => {
        const pts = ds.data
        if (!Array.isArray(pts) || !pts.length) return
        const tip = pts[pts.length - 1]
        if (tip && tip._liveTip) tip.x = now
      })
      chart.update("none")
    })
  }

  _chartOptions({ legend = false } = {}) {
    const self = this
    const now = Date.now()
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      interaction: { mode: "nearest", axis: "x", intersect: false },
      scales: {
        x: {
          type: "linear",
          min: now - WINDOW_MS,
          max: now,
          display: true,
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            font: { size: 10 },
            callback(value) {
              return self._formatTick(value)
            }
          },
          grid: { display: false }
        },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 10 } },
          grid: { color: "rgba(0,0,0,0.06)" }
        }
      },
      plugins: {
        legend: { display: legend, position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } },
        tooltip: {
          enabled: true,
          callbacks: {
            title(items) {
              const x = items?.[0]?.parsed?.x
              return self._formatTooltipTime(x)
            },
            label(item) {
              const name = item.dataset.label || ""
              const val = item.parsed?.y ?? item.raw?.y
              return name ? `${name}: ${val}` : String(val)
            }
          }
        }
      }
    }
  }

  _formatTick(value) {
    const d = this._parseDate(value)
    if (!d) return ""
    return d.toLocaleTimeString(this.i18n.locale, { hour: "2-digit", minute: "2-digit" })
  }

  _formatTooltipTime(value) {
    const d = this._parseDate(value)
    if (!d) return ""
    return d.toLocaleString(this.i18n.locale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })
  }

  _parseDate(value) {
    if (value == null || value === "") return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
    if (typeof value === "number") {
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? null : d
    }
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }

  _pointsFromSeries(values, labels, { liveTip = true } = {}) {
    const labs = labels || this.state?.labels || []
    const vals = values || []
    const points = []
    const len = Math.min(labs.length, vals.length)
    for (let i = 0; i < len; i += 1) {
      const d = this._parseDate(labs[i])
      if (!d) continue
      points.push({ x: d.getTime(), y: Number(vals[i]) || 0 })
    }
    if (liveTip) {
      const y = points.length ? points[points.length - 1].y : 0
      points.push({ x: Date.now(), y, _liveTip: true })
    }
    return points
  }

  _pointsFromQueueHistory(history) {
    const now = Date.now()
    const start = now - WINDOW_MS
    const points = (history || []).map((h) => {
      const d = this._parseDate(h.at)
      if (!d) return null
      const x = d.getTime()
      if (x < start - 60_000) return null
      return { x, y: h.totals?.visible ?? 0 }
    }).filter(Boolean).sort((a, b) => a.x - b.x)

    // Always anchor the left edge of the 60m viewport so the area isn't clipped.
    if (!points.length || points[0].x > start + 1000) {
      points.unshift({ x: start, y: 0 })
    } else if (points[0].x > start) {
      points.unshift({ x: start, y: points[0].y })
    }

    const y = points[points.length - 1]?.y ?? 0
    points.push({ x: now, y, _liveTip: true })
    return points
  }

  _subscribe() {
    this._setStatus(this.t("live_cable_connecting"))
    this.subscription = subscribe(
      { channel: "Admin::LiveOpsChannel" },
      {
        connected: () => this._setStatus(this.t("live_cable_live")),
        disconnected: () => this._setStatus(this.t("live_cable_disconnected")),
        rejected: () => this._setStatus(this.t("live_cable_rejected")),
        received: (data) => this._onMessage(data)
      }
    )
  }

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _onMessage(data) {
    if (!data?.type) return
    if (data.type === "snapshot") {
      this.state = data
      this._renderAll()
      return
    }
    if (!this.state) return
    this._ensureCurrentMinute()
    switch (data.type) {
      case "chat.tick":
        this._bump("team_chat", "messages_per_minute")
        break
      case "email.tick":
        this._bump("email", data.direction === "outbound" ? "outbound_per_minute" : "inbound_per_minute")
        break
      case "users.online":
        this.state.users.online_people = data.count
        if (this.hasUsersOnlineTarget) this.usersOnlineTarget.textContent = data.count
        break
      case "ai.tick":
        if (data.role === "user") this._bump("ai_chat", "user_per_minute")
        else if (data.role === "assistant") this._bump("ai_chat", "assistant_per_minute")
        else if (data.role === "conversation") this._bump("ai_chat", "new_conversations_per_minute")
        break
      case "messenger.tick":
        this._bump("messenger", data.kind === "bot" ? "bot_per_minute" : "user_per_minute")
        break
      case "messenger.state":
        Object.assign(this.state.messenger, data)
        this._renderMessengerKpis()
        break
      case "support.tick":
        this._bump("support", data.kind === "reply" ? "reply_per_minute" : "inbound_per_minute")
        break
      case "support.status":
        Object.assign(this.state.support, data)
        this._renderSupportKpis()
        break
      case "queues.sample":
        this._applyQueuesSample(data)
        break
      case "call_center.session_upserted":
        this._upsertCallSession(data)
        break
      case "call_center.session_ended":
        this._endCallSession(data)
        break
      default:
        break
    }
  }

  _ensureCurrentMinute() {
    if (!this.state) return false
    let advanced = false

    for (let guard = 0; guard < 120; guard += 1) {
      const labels = this.state.labels || []
      const last = labels[labels.length - 1]
      const nowMinute = new Date()
      nowMinute.setSeconds(0, 0)
      const lastMs = last ? new Date(last).getTime() : 0
      if (last && lastMs >= nowMinute.getTime() - 1000) break

      let nextIso = nowMinute.toISOString()
      if (last) {
        const step = new Date(lastMs + 60_000)
        step.setSeconds(0, 0)
        if (step.getTime() < nowMinute.getTime()) nextIso = step.toISOString()
      }

      this.state.labels = [...labels.slice(1), nextIso]
      SERIES_KEYS.forEach(([domain, key]) => {
        const arr = this.state[domain]?.[key]
        if (!Array.isArray(arr)) return
        // Rate counters start at 0; concurrent call load carries forward.
        const pad = domain === "call_center" ? (arr[arr.length - 1] || 0) : 0
        this.state[domain][key] = [...arr.slice(1), pad]
      })
      advanced = true
    }
    return advanced
  }

  _refreshAllCharts() {
    this._updateChart("team_chat")
    this._updateChart("email")
    this._updateChart("ai_chat")
    this._updateChart("messenger")
    this._updateChart("support")
    this._renderCallChart()
    this._renderQueuesChart()
  }

  _bump(domain, key) {
    const arr = this.state[domain]?.[key]
    if (!Array.isArray(arr) || !arr.length) return
    arr[arr.length - 1] = (arr[arr.length - 1] || 0) + 1
    this._updateChart(domain)
  }

  _renderAll() {
    this._lineChart(
      "teamChat",
      this.teamChatChartTarget,
      this.state.team_chat.messages_per_minute,
      "#0d6efd",
      this.t("live_team_chat")
    )
    if (this.hasTeamChatOnlineTarget) this.teamChatOnlineTarget.textContent = this.state.team_chat.online_people ?? 0

    this._multiChart("email", this.emailChartTarget, [
      { data: this.state.email.inbound_per_minute, label: this.t("live_inbound"), color: "#198754" },
      { data: this.state.email.outbound_per_minute, label: this.t("live_outbound"), color: "#fd7e14" }
    ])
    if (this.hasEmailInboundTarget) this.emailInboundTarget.textContent = this.state.email.inbound_total ?? 0
    if (this.hasEmailOutboundTarget) this.emailOutboundTarget.textContent = this.state.email.outbound_total ?? 0

    if (this.hasUsersOnlineTarget) this.usersOnlineTarget.textContent = this.state.users.online_people ?? 0
    if (this.hasUsersSessionsTarget) this.usersSessionsTarget.textContent = this.state.users.active_sessions ?? 0

    this._multiChart("ai", this.aiChartTarget, [
      { data: this.state.ai_chat.user_per_minute, label: this.t("live_user"), color: "#6610f2" },
      { data: this.state.ai_chat.assistant_per_minute, label: this.t("live_assistant"), color: "#d63384" }
    ])
    if (this.hasAiMessagesTarget) this.aiMessagesTarget.textContent = this.state.ai_chat.messages_60m ?? 0
    if (this.hasAiCostTarget) this.aiCostTarget.textContent = (this.state.ai_chat.cost_60m ?? 0).toFixed(4)

    this._multiChart("messenger", this.messengerChartTarget, [
      { data: this.state.messenger.user_per_minute, label: this.t("live_user"), color: "#0dcaf0" },
      { data: this.state.messenger.bot_per_minute, label: this.t("live_bot"), color: "#20c997" }
    ])
    this._renderMessengerKpis()

    this._multiChart("support", this.supportChartTarget, [
      { data: this.state.support.inbound_per_minute, label: this.t("live_inbound"), color: "#dc3545" },
      { data: this.state.support.reply_per_minute, label: this.t("live_reply"), color: "#6c757d" }
    ])
    this._renderSupportKpis()

    this._renderQueues()
    this._renderCallCenter()
  }

  _renderMessengerKpis() {
    if (this.hasMessengerActiveTarget) this.messengerActiveTarget.textContent = this.state.messenger.active ?? 0
    if (this.hasMessengerHumanTarget) this.messengerHumanTarget.textContent = this.state.messenger.human_controlled ?? 0
  }

  _renderSupportKpis() {
    if (this.hasSupportOpenTarget) this.supportOpenTarget.textContent = this.state.support.openish ?? 0
    if (this.hasSupportUnassignedTarget) this.supportUnassignedTarget.textContent = this.state.support.unassigned ?? 0
  }

  _renderQueuesChart() {
    if (!this.hasQueuesChartTarget) return
    this._upsertChart("queues", this.queuesChartTarget, {
      type: "line",
      data: {
        datasets: [{
          label: this.t("live_queue_visible"),
          data: this._pointsFromQueueHistory(this.state.queues?.history),
          borderColor: "#212529",
          backgroundColor: "#21252933",
          fill: true,
          tension: 0.25,
          pointRadius: 0,
          pointHoverRadius: 4
        }]
      },
      options: this._chartOptions({ legend: false })
    })
  }

  _renderQueues() {
    const q = this.state.queues || {}
    if (this.hasQueuesStatusTarget) {
      this.queuesStatusTarget.textContent = q.available ? this.t("live_sqs_ok") : this.t("live_sqs_unavailable")
    }
    this._renderQueuesChart()
    if (this.hasQueuesListTarget) {
      const rows = q.queues || []
      if (!rows.length) {
        this.queuesListTarget.innerHTML =
          `<tr><td colspan="4" class="text-muted">${this.t("live_sqs_unavailable")}</td></tr>`
        return
      }
      this.queuesListTarget.innerHTML = rows.map((row) => {
        const muted = row.error ? "text-muted" : ""
        const fmt = (v) => (v == null || row.error ? "—" : v)
        return `<tr class="${muted}">
          <td data-label="${this.t("live_queue_name")}">
            <code class="small">${row.key}</code>
            ${row.name ? `<div class="text-muted small">${row.name}</div>` : ""}
            ${row.error ? `<div class="small text-danger">${row.error_message || this.t("live_queue_error")}</div>` : ""}
          </td>
          <td class="text-end" data-label="${this.t("live_queue_visible")}">${fmt(row.visible)}</td>
          <td class="text-end" data-label="${this.t("live_queue_inflight")}">${fmt(row.in_flight)}</td>
          <td class="text-end" data-label="${this.t("live_queue_delayed")}">${fmt(row.delayed)}</td>
        </tr>`
      }).join("")
    }
  }

  _applyQueuesSample(data) {
    this.state.queues.available = data.available
    this.state.queues.queues = data.queues
    const hist = Array.isArray(this.state.queues.history) ? [...this.state.queues.history] : []
    hist.push({
      at: data.at,
      available: data.available,
      totals: {
        visible: (data.queues || []).reduce((s, q) => s + (q.visible || 0), 0),
        in_flight: (data.queues || []).reduce((s, q) => s + (q.in_flight || 0), 0)
      }
    })
    // Keep by time window — never slice(-60) or 15s live ticks erase the CloudWatch seed.
    const cutoff = Date.now() - WINDOW_MS
    this.state.queues.history = hist.filter((point) => {
      const d = this._parseDate(point.at)
      return d && d.getTime() >= cutoff
    })
    this._renderQueues()
  }

  _phaseLabel(phase) {
    const key = `live_phase_${phase}`
    const translated = this.t(key)
    return translated.startsWith("missing.") || translated === key ? (phase || "—") : translated
  }

  _renderCallChart() {
    if (!this.hasCallChartTarget) return
    this._lineChart(
      "call",
      this.callChartTarget,
      this.state.call_center?.concurrent_per_minute || [],
      "#ffc107",
      this.t("live_cc_in_progress")
    )
  }

  _renderCallCenter() {
    const cc = this.state.call_center || {}
    if (this.hasCallCountsTarget) {
      const c = cc.counts || {}
      this.callCountsTarget.innerHTML = [
        ["live_cc_in_progress", c.in_progress],
        ["live_cc_ivr", c.in_ivr],
        ["live_cc_ai", c.with_ai],
        ["live_cc_operator", c.with_operator],
        ["live_cc_ringing", c.ringing_operators]
      ].map(([key, v]) => `<span class="badge text-bg-secondary me-1">${this.t(key)}: ${v ?? 0}</span>`).join("")
    }
    this._renderCallChart()
    if (this.hasCallSessionsTarget) {
      const rows = (cc.sessions || []).map((s) => `
        <tr>
          <td>${s.tenant_name || s.tenant_id || "—"}</td>
          <td><code>${s.caller_phone_number || ""}</code></td>
          <td><span class="badge text-bg-info">${this._phaseLabel(s.phase)}</span></td>
          <td>${s.node_label || s.node_type || "—"}</td>
          <td>${s.accepted_by_name || "—"}</td>
        </tr>`).join("")
      this.callSessionsTarget.innerHTML = rows ||
        `<tr><td colspan="5" class="text-muted">${this.t("live_no_active_calls")}</td></tr>`
    }
  }

  _upsertCallSession(data) {
    if (data.counts) this.state.call_center.counts = data.counts
    const sessions = this.state.call_center.sessions || []
    const idx = sessions.findIndex((s) => s.id === data.session?.id)
    if (idx >= 0) sessions[idx] = data.session
    else sessions.unshift(data.session)
    this.state.call_center.sessions = sessions
    const arr = this.state.call_center.concurrent_per_minute
    if (Array.isArray(arr) && arr.length) arr[arr.length - 1] = data.counts?.in_progress ?? arr[arr.length - 1]
    this._renderCallCenter()
  }

  _endCallSession(data) {
    if (data.counts) this.state.call_center.counts = data.counts
    this.state.call_center.sessions = (this.state.call_center.sessions || []).filter((s) => s.id !== data.session_id)
    const arr = this.state.call_center.concurrent_per_minute
    if (Array.isArray(arr) && arr.length) {
      arr[arr.length - 1] = data.counts?.in_progress ?? Math.max(0, (arr[arr.length - 1] || 1) - 1)
    }
    this._renderCallCenter()
  }

  _updateChart(domain) {
    const map = {
      team_chat: () => this._lineChart(
        "teamChat", this.teamChatChartTarget, this.state.team_chat.messages_per_minute, "#0d6efd", this.t("live_team_chat")
      ),
      email: () => this._multiChart("email", this.emailChartTarget, [
        { data: this.state.email.inbound_per_minute, label: this.t("live_inbound"), color: "#198754" },
        { data: this.state.email.outbound_per_minute, label: this.t("live_outbound"), color: "#fd7e14" }
      ]),
      ai_chat: () => this._multiChart("ai", this.aiChartTarget, [
        { data: this.state.ai_chat.user_per_minute, label: this.t("live_user"), color: "#6610f2" },
        { data: this.state.ai_chat.assistant_per_minute, label: this.t("live_assistant"), color: "#d63384" }
      ]),
      messenger: () => this._multiChart("messenger", this.messengerChartTarget, [
        { data: this.state.messenger.user_per_minute, label: this.t("live_user"), color: "#0dcaf0" },
        { data: this.state.messenger.bot_per_minute, label: this.t("live_bot"), color: "#20c997" }
      ]),
      support: () => this._multiChart("support", this.supportChartTarget, [
        { data: this.state.support.inbound_per_minute, label: this.t("live_inbound"), color: "#dc3545" },
        { data: this.state.support.reply_per_minute, label: this.t("live_reply"), color: "#6c757d" }
      ])
    }
    map[domain]?.()
  }

  _lineChart(key, canvas, data, color, seriesLabel) {
    if (!canvas) return
    this._upsertChart(key, canvas, {
      type: "line",
      data: {
        datasets: [{
          label: seriesLabel || key,
          data: this._pointsFromSeries(data),
          borderColor: color,
          backgroundColor: color + "33",
          fill: true,
          tension: 0.25,
          pointRadius: 0,
          pointHoverRadius: 4
        }]
      },
      options: this._chartOptions({ legend: false })
    })
  }

  _multiChart(key, canvas, series) {
    if (!canvas) return
    this._upsertChart(key, canvas, {
      type: "line",
      data: {
        datasets: series.map((s) => ({
          label: s.label,
          data: this._pointsFromSeries(s.data),
          borderColor: s.color,
          backgroundColor: s.color + "22",
          fill: false,
          tension: 0.25,
          pointRadius: 0,
          pointHoverRadius: 4
        }))
      },
      options: this._chartOptions({ legend: true })
    })
  }

  _upsertChart(key, canvas, cfg) {
    if (this.charts[key]) {
      const chart = this.charts[key]
      chart.data.datasets = cfg.data.datasets
      chart.options = cfg.options
      chart.update("none")
      return
    }
    this.charts[key] = new Chart(canvas.getContext("2d"), cfg)
  }
}
